/**
 * @typedef {Object} CommandExecution
 * @property {string} id
 * @property {string} commandId
 * @property {string} createdAt
 * @property {string=} startedAt
 * @property {string=} endedAt
 * @property {string} exitType
 */

/**
 * @typedef {Object} CommandExecutionService
 * @property {Object} filters
 * @property {function(): angular.IPromise<CommandExecution[]>} get
 * @property {function(): angular.IPromise<CommandExecution[]>} next
 * @property {function(): void} reset
 * @property {function(): boolean} hasNextPage
 */

const mod = require('../bahabot.module.js');

/**
 * @param {angular.resource.IResourceService} $resource
 * @returns {CommandExecutionService}
 */
function bahabotCommandExecutionFactory($resource) {
    const CommandExecutions = $resource('/api/protected/command-executions', {}, {
        fetch: { method: 'GET', isArray: false },
    });

    const CommandExecution = $resource('/api/protected/command-executions/:id', {}, {
        get: { method: 'GET', isArray: false },
    });

    const self = {};

    // Public input (bound by controller via ng-model)
    self.filters = {
        commandId: '',
    };

    // Private cursor tracking
    self.lastFilters = null;

    self.data = { items: [], pageInfo: {} };
    self.loading = false;
    self.loadingMore = false;

    /**
     * Get a single execution, filters do not apply
     */
    self.get = (id) => {
        return CommandExecution.get({ id }).$promise.then(res => res.data);
    }

    /**
     * Load first page (uses current public filters)
     */
    self.fetch = () => {
        self.loading = true;

        const query = {
            commandId: self.filters.commandId,
        };

        return CommandExecutions
            .fetch(query)
            .$promise
            .then(res => {
                self.data = res.data;
                self.lastFilters = {
                    commandId: self.filters.commandId,
                    cursor: res.data.pageInfo.endCursor,
                };
                return res.data.items;
            })
            .finally(() => {
                self.loading = false;
            });
    };

    /**
     * Load next page based on lastFilters
     */
    self.next = () => {
        if (!self.hasNextPage()) return Promise.resolve([]);

        self.loadingMore = true;

        const query = {
            commandId: self.lastFilters.commandId,
            cursor: self.lastFilters.cursor,
        };

        return CommandExecutions
            .fetch(query)
            .$promise
            .then(res => {
                self.data.items = self.data.items.concat(res.data.items);
                self.data.pageInfo = res.data.pageInfo;
                self.lastFilters.cursor = res.data.pageInfo.endCursor;
                return res.data.items;
            })
            .finally(() => {
                self.loadingMore = false;
            });
    };

    /**
     * Reset all filters and pagination state
     */
    self.reset = () => {
        self.filters.commandId = '';
        self.lastFilters = null;
        self.data = { items: [], pageInfo: {} };
    };

    /**
     * Indicates if another page is available
     */
    self.hasNextPage = () => {
        return !!self.data.pageInfo?.hasNextPage;
    };

    return self;
}

mod.factory('CommandExecutionService', ["$resource", bahabotCommandExecutionFactory]);
